import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
} from '@mui/material';
import {
  Phone,
  Email,
  Note,
  Event,
  Edit,
  Delete,
} from '@mui/icons-material';
import { adminAPI } from '../../services/api';

// Helper to get current user ID
const getCurrentUserId = () => {
  try {
    const userStr = sessionStorage.getItem('barebiologics_currentUser');
    if (userStr) {
      const user = JSON.parse(userStr);
      return user.id;
    }
  } catch (e) {
    console.error('Error getting current user:', e);
  }
  return null;
};

const ClientTimeline = ({ client, onActivityAdded }) => {
  const [activityDialogOpen, setActivityDialogOpen] = useState(false);
  const [activityType, setActivityType] = useState(null);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Form states
  const [callForm, setCallForm] = useState({ description: '', outcome: '', followUpDate: '' });
  const [emailForm, setEmailForm] = useState({ description: '', subject: '' });
  const [meetingForm, setMeetingForm] = useState({ description: '', date: '', attendees: '' });
  const [noteForm, setNoteForm] = useState({ content: '', isInternal: true });
  const [editingNote, setEditingNote] = useState(null);
  const [editingActivity, setEditingActivity] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);
  const [activityToDelete, setActivityToDelete] = useState(null);

  const currentUserId = getCurrentUserId();

  // Parse internalNotes to extract activities and notes
  const parseInternalNotes = React.useCallback((notes) => {
    if (!notes || !notes.trim()) {
      return { activities: [], notes: [], plainText: '' };
    }

    try {
      // Try to parse as JSON first (structured data)
      const parsed = JSON.parse(notes);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        // Validate structure - must have activities and notes arrays
        if (Array.isArray(parsed.activities) && Array.isArray(parsed.notes)) {
          return {
            activities: parsed.activities || [],
            notes: parsed.notes || [],
            plainText: parsed.plainText || ''
          };
        }
        // If it's an object but not the right structure, treat as plain text
        return { activities: [], notes: [], plainText: notes };
      }
      // If it's an array or other JSON type, treat as plain text
      return { activities: [], notes: [], plainText: notes };
    } catch (e) {
      // Not JSON, treat as plain text
      return { activities: [], notes: [], plainText: notes };
    }
  }, []);

  // Get current structured data from client
  const getStructuredData = React.useCallback(() => {
    const rawNotes = client?.profile?.internalNotes || client?.internalNotes || '';
    console.log('Getting structured data from client:', { 
      clientId: client?.id,
      profileInternalNotes: client?.profile?.internalNotes,
      topLevelInternalNotes: client?.internalNotes,
      rawNotes 
    });
    return parseInternalNotes(rawNotes);
  }, [client, parseInternalNotes]);

  const getActivityIcon = (type) => {
    switch (type) {
      case 'CALL': return <Phone />;
      case 'EMAIL': return <Email />;
      case 'MEETING': return <Event />;
      case 'NOTE': return <Note />;
      default: return <Note />;
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'CALL': return '#2196f3';
      case 'EMAIL': return '#9c27b0';
      case 'MEETING': return '#ff9800';
      case 'NOTE': return '#4caf50';
      default: return '#757575';
    }
  };

  const formatActivityType = (type) => {
    return type
      .split('_')
      .map(word => word.charAt(0) + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Combine activities and notes, sort by date
  const timelineItems = React.useMemo(() => {
    const items = [];
    const data = getStructuredData();
    console.log('Parsed structured data:', data);
    
    // Add activities
    if (data.activities && Array.isArray(data.activities)) {
      data.activities.forEach(activity => {
        if (activity && activity.id && activity.activityType) {
          items.push({
            type: 'activity',
            id: activity.id,
            activityType: activity.activityType,
            description: activity.description || '',
            createdAt: activity.createdAt,
            userId: activity.userId,
            metadata: activity.metadata || null,
          });
        }
      });
    }
    
    // Add notes
    if (data.notes && Array.isArray(data.notes)) {
      data.notes.forEach(note => {
        if (note && note.id && note.content) {
          items.push({
            type: 'note',
            id: note.id,
            content: note.content,
            isInternal: note.isInternal !== false,
            createdAt: note.createdAt,
            userId: note.userId,
            updatedAt: note.updatedAt,
          });
        }
      });
    }
    
    // If there's plain text and no structured data, show it as a note
    if (data.plainText && data.plainText.trim() && items.length === 0) {
      items.push({
        type: 'note',
        id: 'legacy-notes',
        content: data.plainText,
        isInternal: true,
        createdAt: client?.updatedAt || client?.createdAt || new Date().toISOString(),
      });
    }
    
    // Sort by date (newest first)
    const sorted = items.sort((a, b) => {
      const dateA = new Date(a.createdAt);
      const dateB = new Date(b.createdAt);
      return dateB.getTime() - dateA.getTime();
    });
    console.log('Timeline items:', sorted);
    return sorted;
  }, [client, getStructuredData]);

  const saveStructuredData = async (newData) => {
    if (!client?.id) return;

    try {
      setLoading(true);
      setError('');
      
      // Convert to JSON string
      const jsonString = JSON.stringify(newData);
      
      // Backend expects internalNotes at the root level, not inside profile
      const payload = {
        internalNotes: jsonString,
      };

      console.log('Saving timeline data:', { clientId: client.id, payload });

      const response = await adminAPI.updateUser(client.id, payload);
      
      console.log('Save response:', response);
      
      if (response.success) {
        // Call the callback to refresh client data
        onActivityAdded();
      } else {
        throw new Error(response.message || 'Failed to save');
      }
    } catch (err) {
      console.error('Failed to save timeline data:', err);
      setError(err.message || 'Failed to save');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleOpenActivityDialog = (type, existingActivity = null) => {
    setActivityType(type);
    setEditingActivity(existingActivity);
    setActivityDialogOpen(true);
    setError('');
    
    if (existingActivity) {
      const metadata = existingActivity.metadata || {};
      if (type === 'CALL') {
        setCallForm({
          description: existingActivity.description || '',
          outcome: metadata.outcome || '',
          followUpDate: metadata.followUpDate || '',
        });
      } else if (type === 'EMAIL') {
        setEmailForm({
          description: existingActivity.description || '',
          subject: metadata.subject || '',
        });
      } else if (type === 'MEETING') {
        setMeetingForm({
          description: existingActivity.description || '',
          date: metadata.date || '',
          attendees: metadata.attendees || '',
        });
      }
    } else {
      setCallForm({ description: '', outcome: '', followUpDate: '' });
      setEmailForm({ description: '', subject: '' });
      setMeetingForm({ description: '', date: '', attendees: '' });
    }
  };

  const handleOpenNoteDialog = (existingNote = null) => {
    setEditingNote(existingNote);
    if (existingNote) {
      setNoteForm({ content: existingNote.content, isInternal: existingNote.isInternal !== false });
    } else {
      setNoteForm({ content: '', isInternal: true });
    }
    setNoteDialogOpen(true);
    setError('');
  };

  const handleLogActivity = async () => {
    if (!activityType) return;

    let description = '';
    let metadata = null;

    switch (activityType) {
      case 'CALL':
        if (!callForm.description.trim()) {
          setError('Description is required');
          return;
        }
        description = callForm.description;
        metadata = {
          outcome: callForm.outcome || null,
          followUpDate: callForm.followUpDate || null,
        };
        break;
      case 'EMAIL':
        if (!emailForm.description.trim()) {
          setError('Description is required');
          return;
        }
        description = emailForm.description;
        metadata = {
          subject: emailForm.subject || null,
        };
        break;
      case 'MEETING':
        if (!meetingForm.description.trim()) {
          setError('Description is required');
          return;
        }
        description = meetingForm.description;
        metadata = {
          date: meetingForm.date || null,
          attendees: meetingForm.attendees || null,
        };
        break;
      default:
        return;
    }

    try {
      setLoading(true);
      setError('');
      
      const data = getStructuredData();
      const activities = [...(data.activities || [])];
      
      if (editingActivity) {
        // Update existing activity
        const index = activities.findIndex(a => a.id === editingActivity.id);
        if (index !== -1) {
          activities[index] = {
            ...activities[index],
            activityType,
            description,
            metadata,
            updatedAt: new Date().toISOString(),
          };
        }
      } else {
        // Create new activity
        const newActivity = {
          id: `activity-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          activityType,
          description,
          metadata,
          userId: currentUserId,
          createdAt: new Date().toISOString(),
        };
        activities.push(newActivity);
      }
      
      const newData = {
        activities,
        notes: data.notes || [],
        plainText: data.plainText || '',
      };
      
      await saveStructuredData(newData);
      
      setActivityDialogOpen(false);
      setActivityType(null);
      setEditingActivity(null);
      // Reset forms
      setCallForm({ description: '', outcome: '', followUpDate: '' });
      setEmailForm({ description: '', subject: '' });
      setMeetingForm({ description: '', date: '', attendees: '' });
    } catch (err) {
      setError(err.message || (editingActivity ? 'Failed to update activity' : 'Failed to log activity'));
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!noteForm.content.trim()) {
      setError('Note content is required');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const data = getStructuredData();
      const notes = [...(data.notes || [])];
      
      if (editingNote) {
        // Update existing note
        const index = notes.findIndex(n => n.id === editingNote.id);
        if (index !== -1) {
          notes[index] = {
            ...notes[index],
            content: noteForm.content,
            isInternal: noteForm.isInternal,
            updatedAt: new Date().toISOString(),
          };
        }
      } else {
        // Create new note
        const newNote = {
          id: `note-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          content: noteForm.content,
          isInternal: noteForm.isInternal,
          userId: currentUserId,
          createdAt: new Date().toISOString(),
        };
        notes.push(newNote);
      }
      
      const newData = {
        activities: data.activities || [],
        notes,
        plainText: data.plainText || '',
      };
      
      await saveStructuredData(newData);
      
      setNoteDialogOpen(false);
      setEditingNote(null);
      setNoteForm({ content: '', isInternal: true });
    } catch (err) {
      setError(err.message || (editingNote ? 'Failed to update note' : 'Failed to add note'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteActivity = async () => {
    if (!activityToDelete) return;

    try {
      setLoading(true);
      setError('');
      
      const data = getStructuredData();
      const activities = (data.activities || []).filter(a => a.id !== activityToDelete.id);
      
      const newData = {
        activities,
        notes: data.notes || [],
        plainText: data.plainText || '',
      };
      
      await saveStructuredData(newData);
      
      setDeleteConfirmOpen(false);
      setActivityToDelete(null);
    } catch (err) {
      setError(err.message || 'Failed to delete activity');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteNote = async () => {
    if (!noteToDelete) return;

    try {
      setLoading(true);
      setError('');
      
      const data = getStructuredData();
      const notes = (data.notes || []).filter(n => n.id !== noteToDelete.id);
      
      const newData = {
        activities: data.activities || [],
        notes,
        plainText: data.plainText || '',
      };
      
      await saveStructuredData(newData);
      
      setDeleteConfirmOpen(false);
      setNoteToDelete(null);
    } catch (err) {
      setError(err.message || 'Failed to delete note');
    } finally {
      setLoading(false);
    }
  };

  const renderActivityForm = () => {
    switch (activityType) {
      case 'CALL':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Call Description *"
              multiline
              rows={3}
              value={callForm.description}
              onChange={(e) => setCallForm({ ...callForm, description: e.target.value })}
              disabled={loading}
              required
            />
            <TextField
              label="Outcome"
              value={callForm.outcome}
              onChange={(e) => setCallForm({ ...callForm, outcome: e.target.value })}
              disabled={loading}
              placeholder="e.g., Left voicemail, Scheduled follow-up"
            />
            <TextField
              label="Follow-up Date"
              type="date"
              value={callForm.followUpDate}
              onChange={(e) => setCallForm({ ...callForm, followUpDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
              disabled={loading}
            />
          </Box>
        );
      case 'EMAIL':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Email Subject"
              value={emailForm.subject}
              onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
              disabled={loading}
            />
            <TextField
              label="Email Description *"
              multiline
              rows={4}
              value={emailForm.description}
              onChange={(e) => setEmailForm({ ...emailForm, description: e.target.value })}
              disabled={loading}
              required
              placeholder="Describe the email content or outcome"
            />
          </Box>
        );
      case 'MEETING':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Meeting Date"
              type="datetime-local"
              value={meetingForm.date}
              onChange={(e) => setMeetingForm({ ...meetingForm, date: e.target.value })}
              InputLabelProps={{ shrink: true }}
              disabled={loading}
            />
            <TextField
              label="Attendees"
              value={meetingForm.attendees}
              onChange={(e) => setMeetingForm({ ...meetingForm, attendees: e.target.value })}
              disabled={loading}
              placeholder="Who attended the meeting?"
            />
            <TextField
              label="Meeting Description *"
              multiline
              rows={4}
              value={meetingForm.description}
              onChange={(e) => setMeetingForm({ ...meetingForm, description: e.target.value })}
              disabled={loading}
              required
              placeholder="Meeting notes and discussion points"
            />
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: 'Poppins, sans-serif' }}>
            Activity Timeline
          </Typography>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Phone />}
            onClick={() => handleOpenActivityDialog('CALL')}
          >
            Log Call
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Email />}
            onClick={() => handleOpenActivityDialog('EMAIL')}
          >
            Log Email
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Event />}
            onClick={() => handleOpenActivityDialog('MEETING')}
          >
            Log Meeting
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Note />}
            onClick={() => handleOpenNoteDialog()}
          >
            Add Note
          </Button>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Timeline List */}
        {timelineItems.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Poppins, sans-serif' }}>
              No activity yet. Log calls, emails, or add notes to track your interactions.
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {timelineItems.map((item, index) => (
              <React.Fragment key={item.id || index}>
                <ListItem 
                  alignItems="flex-start" 
                  sx={{ px: 0 }}
                  secondaryAction={
                    (item.type === 'note' && (item.userId === currentUserId || item.id === 'legacy-notes')) ||
                    (item.type === 'activity' && 
                     item.userId === currentUserId &&
                     ['CALL', 'EMAIL', 'MEETING'].includes(item.activityType)) ? (
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => {
                            if (item.type === 'note') {
                              if (item.id === 'legacy-notes') {
                                // Can't edit legacy notes, only delete
                                return;
                              }
                              handleOpenNoteDialog(item);
                            } else {
                              handleOpenActivityDialog(item.activityType, item);
                            }
                          }}
                          title={item.type === 'note' ? (item.id === 'legacy-notes' ? 'Cannot edit legacy notes' : 'Edit note') : 'Edit activity'}
                          disabled={item.id === 'legacy-notes'}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => {
                            if (item.type === 'note') {
                              setNoteToDelete(item);
                              setDeleteConfirmOpen(true);
                            } else {
                              setActivityToDelete(item);
                              setDeleteConfirmOpen(true);
                            }
                            setError('');
                          }}
                          title={item.type === 'note' ? 'Delete note' : 'Delete activity'}
                          sx={{ color: 'error.main' }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    ) : null
                  }
                >
                  <ListItemAvatar>
                    <Avatar
                      sx={{
                        bgcolor: item.type === 'activity' ? getActivityColor(item.activityType) : '#4caf50',
                        width: 40,
                        height: 40,
                      }}
                    >
                      {item.type === 'activity' 
                        ? getActivityIcon(item.activityType)
                        : <Note />
                      }
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="subtitle2" sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600 }}>
                          {item.type === 'activity' 
                            ? formatActivityType(item.activityType)
                            : item.isInternal ? 'Internal Note' : 'Note'
                          }
                        </Typography>
                        {item.isInternal && (
                          <Chip label="Internal" size="small" color="warning" />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" sx={{ fontFamily: 'Poppins, sans-serif', mb: 0.5, whiteSpace: 'pre-wrap' }}>
                          {item.type === 'activity' ? item.description : item.content}
                        </Typography>
                        {item.type === 'activity' && item.metadata && (
                          <Box sx={{ mt: 0.5, mb: 0.5 }}>
                            {item.metadata.outcome && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                Outcome: {item.metadata.outcome}
                              </Typography>
                            )}
                            {item.metadata.followUpDate && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                Follow-up: {new Date(item.metadata.followUpDate).toLocaleDateString()}
                              </Typography>
                            )}
                            {item.metadata.subject && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                Subject: {item.metadata.subject}
                              </Typography>
                            )}
                            {item.metadata.date && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                Date: {new Date(item.metadata.date).toLocaleString()}
                              </Typography>
                            )}
                            {item.metadata.attendees && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                Attendees: {item.metadata.attendees}
                              </Typography>
                            )}
                          </Box>
                        )}
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                          {new Date(item.createdAt).toLocaleString()}
                          {item.type === 'note' && item.updatedAt && new Date(item.updatedAt).getTime() > new Date(item.createdAt).getTime() && (
                            <span> (edited)</span>
                          )}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
                {index < timelineItems.length - 1 && <Divider variant="inset" component="li" />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>

      {/* Activity Dialog */}
      <Dialog 
        open={activityDialogOpen} 
        onClose={() => {
          setActivityDialogOpen(false);
          setEditingActivity(null);
          setActivityType(null);
          setError('');
        }} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          {editingActivity ? 'Edit' : 'Log'} {activityType ? formatActivityType(activityType) : 'Activity'}
        </DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {renderActivityForm()}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setActivityDialogOpen(false);
              setEditingActivity(null);
              setActivityType(null);
              setError('');
            }} 
            disabled={loading}
          >
            Cancel
          </Button>
          <Button onClick={handleLogActivity} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : (editingActivity ? 'Update Activity' : 'Log Activity')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Note Dialog */}
      <Dialog open={noteDialogOpen} onClose={() => {
        setNoteDialogOpen(false);
        setEditingNote(null);
        setNoteForm({ content: '', isInternal: true });
        setError('');
      }} maxWidth="sm" fullWidth>
        <DialogTitle>{editingNote ? 'Edit Note' : 'Add Note'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Note Content *"
              multiline
              rows={5}
              value={noteForm.content}
              onChange={(e) => setNoteForm({ ...noteForm, content: e.target.value })}
              disabled={loading}
              required
              placeholder="Enter your note here..."
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <input
                type="checkbox"
                id="isInternal"
                checked={noteForm.isInternal}
                onChange={(e) => setNoteForm({ ...noteForm, isInternal: e.target.checked })}
                disabled={loading}
              />
              <label htmlFor="isInternal" style={{ fontFamily: 'Poppins, sans-serif', fontSize: '14px' }}>
                Internal note (visible to admins only)
              </label>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setNoteDialogOpen(false);
            setEditingNote(null);
            setNoteForm({ content: '', isInternal: true });
            setError('');
          }} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleAddNote} 
            variant="contained" 
            disabled={loading || !noteForm.content.trim()}
          >
            {loading ? <CircularProgress size={20} /> : (editingNote ? 'Update Note' : 'Add Note')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteConfirmOpen} 
        onClose={() => {
          setDeleteConfirmOpen(false);
          setNoteToDelete(null);
          setActivityToDelete(null);
          setError('');
        }} 
        maxWidth="xs" 
        fullWidth
      >
        <DialogTitle>
          Delete {noteToDelete ? 'Note' : activityToDelete ? formatActivityType(activityToDelete.activityType) : 'Item'}
        </DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Typography sx={{ fontFamily: 'Poppins, sans-serif' }}>
            Are you sure you want to delete this {noteToDelete ? 'note' : activityToDelete ? formatActivityType(activityToDelete.activityType).toLowerCase() : 'item'}? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setDeleteConfirmOpen(false);
              setNoteToDelete(null);
              setActivityToDelete(null);
              setError('');
            }} 
            disabled={loading}
          >
            Cancel
          </Button>
          <Button 
            onClick={noteToDelete ? handleDeleteNote : handleDeleteActivity} 
            variant="contained" 
            color="error" 
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ClientTimeline;
